import { useAuth } from './useAuth';
import { useInternalAuth } from './useInternalAuth';
import { useMemo } from 'react';

export interface UnifiedUser {
  id: string;
  username: string;
  userType: 'supabase' | 'internal';
  email?: string;
  role?: string;
  profile?: any;
}

export const useUnifiedUser = () => {
  const { user: supabaseUser, profile: supabaseProfile, loading: supabaseLoading, initialCheckComplete } = useAuth();
  const { user: internalUser, profile: internalProfile, loading: internalLoading } = useInternalAuth();

  // Use stable ID-based dependencies to prevent unnecessary object recreation
  const supabaseUserId = supabaseUser?.id;
  const internalUserId = internalUser?.id;
  const supabaseProfileId = supabaseProfile?.id;
  const internalProfileUserId = internalProfile?.user_id;
  const internalUserRole = internalUser?.role;
  const supabaseProfileRole = supabaseProfile?.role;
  const internalUserUsername = internalUser?.username;
  const supabaseProfileUsername = supabaseProfile?.username;
  const supabaseUserEmail = supabaseUser?.email;
  
  const unifiedUser = useMemo((): UnifiedUser | null => {
    // Prioritize internal user to resolve conflicts
    if (internalUser) {
      return {
        id: internalUser.id,
        username: internalUser.username || 'New School User',
        userType: 'internal' as const,
        role: internalUser.role || 'player',
        profile: internalProfile
      };
    }
    
    if (supabaseUser) {
      const username = supabaseProfile?.username || 
                     supabaseUser.user_metadata?.username || 
                     supabaseUser.email?.split('@')[0] ||
                     'Supabase User';
      
      return {
        id: supabaseUser.id,
        username,
        userType: 'supabase' as const,
        email: supabaseUser.email || '',
        role: supabaseProfile?.role || 'player',
        profile: supabaseProfile
      };
    }
    
    return null;
  }, [supabaseUserId, internalUserId, supabaseProfileId, internalProfileUserId, 
      internalUserRole, supabaseProfileRole, internalUserUsername, supabaseProfileUsername, 
      supabaseUserEmail, supabaseUser, internalUser, supabaseProfile, internalProfile]);

  // Simple loading logic - don't block on role fetching
  const hasUser = !!internalUser || !!supabaseUser;
  const isLoading = !initialCheckComplete || (hasUser && (supabaseLoading || internalLoading));

  return {
    user: unifiedUser,
    isLoading,
    isSupabaseUser: !!supabaseUser && !internalUser,
    isInternalUser: !!internalUser,
    supabaseUser,
    internalUser,
    supabaseProfile,
    internalProfile
  };
};
